import React from 'react';

import { Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

export interface TogglePillOption<T extends string> {
  value: T;
  label: string;
}

interface Props<T extends string> {
  label: string;
  ariaLabel: string;
  options: ReadonlyArray<TogglePillOption<T>>;
  value: T;
  onChange: (next: T) => void;
}

// Separated blue-selected pills — shared across the PriceParrot chain dashboards.
const PILL_SX = {
  gap: 1,
  '& .MuiToggleButtonGroup-grouped': {
    border: 0,
    // !important required: MUI zeroes border-radius on middle buttons in grouped mode;
    // these pills are visually separated (gap: 1) so each needs its own radius.
    borderRadius: '8px !important',
    bgcolor: 'background.paper',
    px: 2,
    py: 0.75,
    fontWeight: 600,
    textTransform: 'none',
    color: 'text.primary',
    '&:hover': { bgcolor: 'background.paper' },
    '&.Mui-selected, &.Mui-selected:hover': {
      bgcolor: 'primary.main',
      color: 'common.white',
    },
  },
} as const;

export default function TogglePillGroup<T extends string>({ label, ariaLabel, options, value, onChange }: Props<T>) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', mr: 1 }}>{label}</Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        size="small"
        onChange={(_, next) => next && onChange(next as T)}
        aria-label={ariaLabel}
        sx={PILL_SX}
      >
        {options.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value}>
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
}
